/**
 * OrgRequestDetail — task detail matched to reference Travel itinerary page.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LpWorkbenchShell from '../components/org/LpWorkbenchShell';
import { orgAuthApi } from '../services/api';
import { getLoginPath, getOrgSlatePath } from '../utils/postLoginNavigation';
import { buildRequestDetailView } from '../utils/lpRequestDetail';
import '../styles/LpWorkbench.css';
import '../styles/LpRequestDetail.css';

export default function OrgRequestDetail() {
  const { t } = useTranslation();
  const { productionId, taskSlug } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [project, setProject] = useState(null);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const mainTabs = useMemo(
    () => [
      { id: 'overview', label: t('lpWorkbench.tabOverview') },
      { id: 'immigration', label: 'Immigration' },
      { id: 'tasks', label: t('lpWorkbench.tabTasks') },
    ],
    [t],
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [tasksRes, prodRes] = await Promise.all([
        orgAuthApi.getProductionTasks(productionId),
        orgAuthApi.getProduction(productionId),
      ]);
      const found = (tasksRes.data?.tasks || []).find((row) => row.slug === taskSlug);
      if (!found) {
        setError('This request was not found.');
        setTask(null);
        return;
      }
      setTask(found);
      setProject(prodRes.data?.project || null);
      setPeople(prodRes.data?.participants || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(getLoginPath(), { replace: true });
        return;
      }
      setError('Could not load this request.');
    } finally {
      setLoading(false);
    }
  }, [productionId, taskSlug, navigate]);

  useEffect(() => { load(); }, [load]);

  const view = useMemo(
    () => buildRequestDetailView(task, 'active', people),
    [task, people],
  );

  function openPerson(id) {
    if (!id) return;
    navigate(`/org/productions/${productionId}/participants/${id}`);
  }

  function goTab(tabId) {
    navigate(`/org/productions/${productionId}?tab=${tabId}`);
  }

  const backHref = `/org/productions/${productionId}?tab=tasks`;
  const pct = view?.total ? Math.round((view.done / view.total) * 100) : 0;

  return (
    <LpWorkbenchShell
      slateTheme
      hideLanguage
      compactProfile
      deleteProductionId={productionId}
      deleteProductionName={project?.name || ''}
    >
      {loading ? (
        <div className="lp-body">
          <div className="lp-empty">Loading…</div>
        </div>
      ) : error ? (
        <div className="lp-body">
          <div className="lp-banner" style={{ background: '#FBF1EF', borderColor: '#E8C4BE' }}>
            <div className="lp-bn-txt"><b>{error}</b></div>
          </div>
        </div>
      ) : view ? (
        <>
          <div className="lp-head">
            <div className="lp-prod-nav">
              <div className="lp-prod-title-row">
                <button
                  type="button"
                  className="lp-prod-back"
                  onClick={() => navigate(getOrgSlatePath())}
                  aria-label="Back to productions"
                >
                  ‹
                </button>
                <h1>{project?.name || 'Production'}</h1>
              </div>
              <nav className="lp-ptabs" aria-label="Production navigation">
                {mainTabs.map((tabItem) => (
                  <button
                    key={tabItem.id}
                    type="button"
                    className={`lp-ptab${tabItem.id === 'tasks' ? ' on' : ''}`}
                    onClick={() => goTab(tabItem.id)}
                  >
                    {tabItem.label}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          <div className="lp-body lp-ref-body">
            <div className="lp-td">
              <Link to={backHref} className="lp-td-back">
                &lt; Tasks
              </Link>

              <div className="lp-td-title">
                <div>
                  <h1>{view.title}</h1>
                  <p>{view.isComplete ? 'Complete' : 'Due before arrival'}</p>
                </div>
                <button type="button" className="lp-td-edit">
                  Edit task
                </button>
              </div>

              <section
                className="lp-td-metrics"
                aria-label={`${view.title} status`}
              >
                <div>
                  <span>Assigned</span>
                  <strong>{view.total}</strong>
                </div>
                <div>
                  <span>Complete</span>
                  <strong>{view.done}</strong>
                </div>
                <div>
                  <span>Outstanding</span>
                  <strong>{view.outstandingCount}</strong>
                </div>
                <div>
                  <span>In Process</span>
                  <strong>{view.inProcessCount}</strong>
                </div>
              </section>

              <div
                className="lp-td-progress"
                aria-label={`${view.done} of ${view.total} complete`}
              >
                <i style={{ width: `${pct}%` }} />
              </div>

              <section className="lp-td-section">
                <header>
                  <h2>Participants</h2>
                </header>
                <div className="lp-td-table">
                  <div className="lp-td-head">
                    <span>Participant</span>
                    <span>Role</span>
                    <span>Location</span>
                    <span>Needed by</span>
                    <span>Status</span>
                  </div>
                  {view.participants.length === 0 ? (
                    <div className="lp-td-empty">No participants assigned yet.</div>
                  ) : (
                    view.participants.map((row) => (
                      <div className="lp-td-row" key={row.id}>
                        <button
                          type="button"
                          className="lp-td-name"
                          onClick={() => openPerson(row.id)}
                        >
                          {row.name}
                        </button>
                        <span>{row.role}</span>
                        <span>{row.location}</span>
                        <span>{row.neededBy}</span>
                        <b className={row.statusClass}>{row.statusLabel}</b>
                      </div>
                    ))
                  )}
                </div>
              </section>
            </div>
          </div>
        </>
      ) : null}
    </LpWorkbenchShell>
  );
}
